/**
 * USSA Cleanup Utilities
 * ======================
 * USSA data cleanup using new USSA API via local proxy.
 * Implements parallel API calls for performance.
 * 
 * All racer data is NORMALIZED at import — only canonical field names exist.
 * This file reads/writes using CANONICAL_FIELDS exclusively.
 * 
 * Run proxy first: cd proxy && npm install && npm start
 * Requires USSA_BEARER_TOKEN env var for the proxy.
 */

import { CANONICAL_FIELDS, getField, hasField } from './columnNormalizer';
import { getMembersByIdsBatch, getMembersRankingsBatch, getPoints } from '../api/ussaApi';
import { fetchFisPoints } from './fisCleanup';

// Batch size for API queries (single request can fetch multiple)
const BATCH_SIZE = 100;  // How many IDs to query per API call (increased)
const DELAY_BETWEEN_BATCHES = 0; // No delay needed with batch queries

/**
 * Calculate age category for USSA minimum points.
 * Age is calculated as of Dec 31 of the season year.
 * 
 * USSA Rule: If race is between Nov-Mar of season 2025-2026,
 * age is calculated as of Dec 31, 2025 (the December within the season).
 * 
 * @param {number|string} yob - Year of birth
 * @param {string} raceDate - Race date in YYYY-MM-DD format
 * @returns {string} Age category: 'U16', 'U18', or 'SENIOR'
 */
function getUssaAgeCategory(yob, raceDate) {
  if (!yob) return 'SENIOR'; // Default to senior if no YOB
  
  const birthYear = parseInt(yob, 10);
  if (isNaN(birthYear)) return 'SENIOR';
  
  // Determine the Dec 31 reference date based on race date
  // Season runs Nov-Mar, so:
  // - Race in Nov/Dec 2025 -> Dec 31, 2025
  // - Race in Jan-Mar 2026 -> still Dec 31, 2025 (same season)
  let refYear;
  if (raceDate) {
    const raceDateObj = new Date(raceDate);
    const raceMonth = raceDateObj.getMonth(); // 0-11
    const raceYear = raceDateObj.getFullYear();
    
    if (raceMonth >= 10) { // Nov or Dec (months 10, 11)
      refYear = raceYear;
    } else if (raceMonth <= 2) { // Jan, Feb, Mar (months 0, 1, 2)
      refYear = raceYear - 1;
    } else {
      // Outside typical season - use current logic
      refYear = raceMonth >= 6 ? raceYear : raceYear - 1;
    }
  } else {
    // No race date - use current season logic
    const now = new Date();
    refYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  }
  
  // Age as of Dec 31 of refYear
  const ageOnDec31 = refYear - birthYear;
  
  // U16 = 15 or younger (will turn 16 in the season year or later)
  // U18 = 16-17 years old
  // Senior = 18+
  if (ageOnDec31 <= 15) return 'U16';
  if (ageOnDec31 <= 17) return 'U18';
  return 'SENIOR';
}

/**
 * Get USSA minimum points for an unranked top-5 finisher.
 * 
 * Rules:
 * - US citizen U18+: Men=200, Women=250
 * - US citizen U16: 350
 * - Foreign citizen: Use FIS points, or above values if no FIS points
 * 
 * @param {string} ageCategory - 'U16', 'U18', or 'SENIOR'
 * @param {string} sex - 'M', 'F', or 'W' (Women)
 * @param {boolean} isUSCitizen - Whether racer is a US citizen
 * @param {number|null} fisPoints - Racer's FIS points (if available)
 * @returns {number} Minimum points to use
 */
function getUssaMinimumPoints(ageCategory, sex, isUSCitizen, fisPoints) {
  // If foreign with FIS points, use those
  if (!isUSCitizen && fisPoints !== null && fisPoints !== undefined && !isNaN(fisPoints)) {
    return fisPoints;
  }
  
  // U16 always gets 350 (regardless of sex)
  if (ageCategory === 'U16') {
    return 350;
  }
  
  // U18 or Senior - depends on sex
  // Header form uses 'W' for Women, API uses 'F' - handle both
  if (sex === 'F' || sex === 'W') {
    return 250;
  }
  return 200;
}

/**
 * Main USSA cleanup function.
 * Uses new USSA API via local proxy with parallel API calls.
 * 
 * @param {Array<Object>} racers - Racer data (normalized — canonical names only)
 * @param {Object} options - Cleanup options
 * @param {string} options.discipline - 'distance' | 'sprint' | 'overall'
 * @param {string} options.listCode - USSA list code (e.g., "26LIST04")
 * @param {number} options.season - Season year (e.g., 2026)
 * @param {Function} options.onProgress - Progress callback
 * @returns {Promise<Object>} { validRacers, removedRacers, changes }
 */
export async function cleanUssaData(racers, options) {
  const { 
    discipline = 'distance', 
    listCode, 
    listNumber,
    fisListNumber,  // FIS list number for FIS points lookup
    season = new Date().getFullYear() + (new Date().getMonth() >= 6 ? 1 : 0),
    sex = 'M',      // 'M' or 'F' for minimum points calculation
    raceDate,       // Race date for age category calculation (YYYY-MM-DD)
    onProgress 
  } = options;
  
  // Build list code if only number provided
  const actualListCode = listCode || (listNumber ? `${String(season).slice(-2)}LIST${String(listNumber).padStart(2, '0')}` : null);
  
  // Map discipline for FIS points lookup
  const fisDiscipline = discipline === 'sprint' ? 'SP' : 'DI';
  
  // Detect if this is a sprint heats file
  const isSprintHeats = racers.some(r => r.highestLevel);
  
  const changes = [];
  const removedRacers = [];
  
  // Step 1: Separate racers with/without USSA codes
  onProgress?.('Filtering racers...');
  const racersWithUssa = racers.filter(r => hasField(r, CANONICAL_FIELDS.USSA_CODE));
  const racersWithoutUssa = racers.filter(r => !hasField(r, CANONICAL_FIELDS.USSA_CODE));
  
  console.log(`[USSA Cleanup] ${racersWithUssa.length} racers with USSA codes, ${racersWithoutUssa.length} without`);
  console.log(`[USSA Cleanup] Using list code: ${actualListCode}, season: ${season}, discipline: ${discipline}`);
  
  // Mark no-USSA racers as needing synthetic codes
  racersWithoutUssa.forEach(r => {
    if (!hasField(r, CANONICAL_FIELDS.FIS_CODE)) {
      removedRacers.push({
        racer: r,
        reason: 'Missing USSA Code (and no FIS code for synthetic)',
      });
    }
  });
  
  if (racersWithUssa.length === 0 && racersWithoutUssa.filter(r => hasField(r, CANONICAL_FIELDS.FIS_CODE)).length === 0) {
    return { validRacers: [], removedRacers, changes };
  }
  
  // Step 2: Batch lookup of member info (single query per batch of 50)
  onProgress?.(`Looking up ${racersWithUssa.length} USSA members...`);
  const ussaCodes = racersWithUssa.map(r => String(getField(r, CANONICAL_FIELDS.USSA_CODE)).trim());
  
  // Fetch member data using batch queries
  const memberDataMap = await fetchMembersBatch(ussaCodes, onProgress);
  
  // Step 3: Batch lookup of rankings/points (if list code provided)
  let rankingsMap = new Map();
  if (actualListCode) {
    onProgress?.(`Looking up rankings for list ${actualListCode}...`);
    rankingsMap = await fetchRankingsBatch(ussaCodes, actualListCode, season, onProgress);
  }
  
  // Step 3b: Lookup FIS points for racers with FIS codes (for penalty calculation fallback)
  let fisPointsMap = new Map();
  const racersWithFis = racers.filter(r => hasField(r, CANONICAL_FIELDS.FIS_CODE));
  
  // Parse FIS list number - could be string from form input
  const parsedFisListNumber = fisListNumber ? parseInt(String(fisListNumber), 10) : null;
  
  console.log(`[USSA Cleanup] FIS points lookup: ${racersWithFis.length} racers with FIS codes, fisListNumber=${fisListNumber}, parsed=${parsedFisListNumber}`);
  
  if (racersWithFis.length > 0 && parsedFisListNumber) {
    onProgress?.(`Looking up FIS points for ${racersWithFis.length} racers (List ${parsedFisListNumber})...`);
    const fisCodes = racersWithFis.map(r => String(getField(r, CANONICAL_FIELDS.FIS_CODE)).trim()).filter(c => c && c !== '');
    
    console.log(`[USSA Cleanup] FIS codes to lookup:`, fisCodes.slice(0, 10));
    
    if (fisCodes.length > 0) {
      try {
        const fisPointsData = await fetchFisPoints(fisCodes, parsedFisListNumber);
        console.log(`[USSA Cleanup] Got FIS points for ${fisPointsData?.length || 0} racers`);
        
        // Log sample of FIS data
        if (fisPointsData?.length > 0) {
          console.log('[USSA Cleanup] Sample FIS data:', fisPointsData[0]);
        }
        
        // Create lookup map by FIS code
        (fisPointsData || []).forEach(p => {
          if (p.fis_licence) {
            fisPointsMap.set(String(p.fis_licence).trim(), p);
          }
        });
        
        console.log(`[USSA Cleanup] FIS points map size: ${fisPointsMap.size}`);
      } catch (err) {
        console.error('[USSA Cleanup] Error fetching FIS points:', err);
        onProgress?.('Warning: Could not fetch FIS points');
      }
    } else {
      console.log('[USSA Cleanup] No valid FIS codes to lookup');
    }
  } else if (racersWithFis.length > 0 && !parsedFisListNumber) {
    console.log('[USSA Cleanup] Skipping FIS points lookup - no FIS list number provided');
    onProgress?.('Note: FIS List not specified - skipping FIS points lookup');
  }
  
  // Step 4: Process racers with API data — write ONLY canonical field names
  onProgress?.('Processing racers...');
  const validRacers = [];
  
  for (const racer of racersWithUssa) {
    const ussaCode = String(getField(racer, CANONICAL_FIELDS.USSA_CODE)).trim();
    const memberData = memberDataMap.get(ussaCode);
    const rankingData = rankingsMap.get(ussaCode);
    
    // Check membership status
    if (memberData) {
      const isActive = checkMembershipActive(memberData, season);
      if (!isActive) {
        removedRacers.push({
          racer,
          reason: 'USSA membership not active for current season',
        });
        continue;
      }
    }
    
    // Apply updates
    const updatedRacer = { ...racer };
    const racerChanges = [];
    
    // Apply member data updates
    if (memberData) {
      // Name updates
      if (memberData.firstName && memberData.firstName !== getField(racer, CANONICAL_FIELDS.FIRST_NAME)) {
        racerChanges.push({
          ussaCode,
          field: 'firstName',
          oldValue: getField(racer, CANONICAL_FIELDS.FIRST_NAME),
          newValue: memberData.firstName,
        });
        updatedRacer[CANONICAL_FIELDS.FIRST_NAME] = memberData.firstName;
      }
      
      if (memberData.lastName && memberData.lastName !== getField(racer, CANONICAL_FIELDS.LAST_NAME)) {
        racerChanges.push({
          ussaCode,
          field: 'lastName',
          oldValue: getField(racer, CANONICAL_FIELDS.LAST_NAME),
          newValue: memberData.lastName,
        });
        updatedRacer[CANONICAL_FIELDS.LAST_NAME] = memberData.lastName;
      }
      
      // YOB from birth year
      if (memberData.birth?.year) {
        const apiYOB = String(memberData.birth.year);
        if (apiYOB !== String(getField(racer, CANONICAL_FIELDS.YOB) || '')) {
          racerChanges.push({
            ussaCode,
            field: 'yob',
            oldValue: getField(racer, CANONICAL_FIELDS.YOB),
            newValue: apiYOB,
          });
          updatedRacer[CANONICAL_FIELDS.YOB] = apiYOB;
        }
      }
      
      // Nation/Country - USSA members are USA unless division is "foreign"
      const currentCountry = getField(racer, CANONICAL_FIELDS.COUNTRY);
      const division = memberData.division?.toLowerCase();
      const isForeign = division === 'foreign';
      
      if (!isForeign) {
        // Non-foreign division = USA athlete
        if (currentCountry !== 'USA') {
          racerChanges.push({
            ussaCode,
            field: 'country',
            oldValue: currentCountry,
            newValue: 'USA',
            note: `Division: ${memberData.division || 'unknown'}`,
          });
          updatedRacer[CANONICAL_FIELDS.COUNTRY] = 'USA';
        }
      } else {
        // Foreign division - use country field from API if available, 
        // otherwise leave for FIS lookup
        const apiCountry = memberData.country?.toUpperCase();
        if (apiCountry && apiCountry !== 'USA' && currentCountry !== apiCountry) {
          racerChanges.push({
            ussaCode,
            field: 'country',
            oldValue: currentCountry,
            newValue: apiCountry,
            note: 'Foreign division - using API country',
          });
          updatedRacer[CANONICAL_FIELDS.COUNTRY] = apiCountry;
        }
        // If no country from API and we have FIS code, leave blank for FIS lookup
      }
    }
    
    // Apply ranking/points data
    if (rankingData) {
      const pointsValue = getPoints(rankingData, discipline);
      if (pointsValue !== null) {
        const currentPoints = getField(racer, CANONICAL_FIELDS.USSA_POINTS);
        if (!currentPoints || parseFloat(currentPoints) !== pointsValue) {
          racerChanges.push({
            ussaCode,
            field: 'ussaPoints',
            oldValue: currentPoints,
            newValue: pointsValue,
          });
          updatedRacer[CANONICAL_FIELDS.USSA_POINTS] = pointsValue;
        }
      }
    }
    
    // Apply FIS points if available (for penalty calculation fallback)
    const fisCode = getField(racer, CANONICAL_FIELDS.FIS_CODE);
    if (fisCode) {
      const fisData = fisPointsMap.get(String(fisCode).trim());
      if (fisData) {
        // Log what we found for this racer
        console.log(`[USSA Cleanup] FIS data for ${fisCode}:`, fisData.fis_points, `discipline=${fisDiscipline}`);
        
        const fisPoints = fisData.fis_points?.[fisDiscipline];
        if (fisPoints !== undefined && fisPoints !== null) {
          const currentFisPoints = getField(racer, CANONICAL_FIELDS.FIS_POINTS);
          console.log(`[USSA Cleanup] Applying FIS points for ${fisCode}: ${fisPoints} (was: ${currentFisPoints})`);
          if (!currentFisPoints || parseFloat(currentFisPoints) !== parseFloat(fisPoints)) {
            racerChanges.push({
              ussaCode,
              fisCode,
              field: 'fisPoints',
              oldValue: currentFisPoints,
              newValue: fisPoints,
            });
            updatedRacer[CANONICAL_FIELDS.FIS_POINTS] = parseFloat(fisPoints);
          }
        } else {
          console.log(`[USSA Cleanup] No FIS points for discipline ${fisDiscipline} for ${fisCode}`);
        }
        
        // Also use FIS data for country if not set (for foreign USSA members)
        const currentCountry = getField(updatedRacer, CANONICAL_FIELDS.COUNTRY);
        if (!currentCountry && fisData.nation) {
          racerChanges.push({
            ussaCode,
            fisCode,
            field: 'country',
            oldValue: null,
            newValue: fisData.nation,
            note: 'From FIS lookup',
          });
          updatedRacer[CANONICAL_FIELDS.COUNTRY] = fisData.nation;
        }
      }
    }
    
    validRacers.push(updatedRacer);
    if (racerChanges.length > 0) {
      changes.push(...racerChanges);
    }
  }
  
  // Step 5: Handle FIS-only racers (generate synthetic USSA codes)
  // Use M + 7-digit padded counter (e.g., M0000001) matching USSA convention for foreign racers
  const fisOnlyRacers = racersWithoutUssa.filter(r => hasField(r, CANONICAL_FIELDS.FIS_CODE));
  let syntheticCounter = 1;
  
  if (fisOnlyRacers.length > 0) {
    onProgress?.(`Processing ${fisOnlyRacers.length} FIS-only racers...`);
    
    for (const racer of fisOnlyRacers) {
      const fisCode = getField(racer, CANONICAL_FIELDS.FIS_CODE);
      const syntheticCode = 'M' + String(syntheticCounter++).padStart(7, '0');
      
      const updatedRacer = { ...racer };
      updatedRacer[CANONICAL_FIELDS.USSA_CODE] = syntheticCode;
      
      changes.push({
        fisCode,
        field: 'ussaCode',
        oldValue: null,
        newValue: syntheticCode,
        note: 'Synthetic USSA code for FIS-only racer',
      });
      
      // Apply FIS points and data if available
      const fisData = fisPointsMap.get(String(fisCode).trim());
      if (fisData) {
        // FIS Points
        const fisPoints = fisData.fis_points?.[fisDiscipline];
        if (fisPoints !== undefined && fisPoints !== null) {
          updatedRacer[CANONICAL_FIELDS.FIS_POINTS] = parseFloat(fisPoints);
          changes.push({
            fisCode,
            field: 'fisPoints',
            oldValue: null,
            newValue: fisPoints,
          });
        }
        
        // Country from FIS
        if (!getField(updatedRacer, CANONICAL_FIELDS.COUNTRY) && fisData.nation) {
          updatedRacer[CANONICAL_FIELDS.COUNTRY] = fisData.nation;
          changes.push({
            fisCode,
            field: 'country',
            oldValue: null,
            newValue: fisData.nation,
            note: 'From FIS lookup',
          });
        }
      }
      
      validRacers.push(updatedRacer);
    }
  }
  
  // Step 6: Re-rank valid racers
  onProgress?.('Re-ranking...');
  if (isSprintHeats) {
    // Sprint heats: preserve original order, just reassign ranks
    reRankSprintRacers(validRacers, racers);
  } else {
    // Distance: sort by time and re-rank.
    // IMPORTANT: Only preserve ties when racers were already tied in input CSV ranks.
    reRankRacers(validRacers);
  }
  
  // Step 7: Apply USSA minimum points for unranked top-5 finishers
  onProgress?.('Applying minimum points for unranked top-5 finishers...');
  applyUssaMinimumPoints(validRacers, changes, {
    sex,
    raceDate,
    discipline: fisDiscipline,
    onProgress,
  });
  
  onProgress?.(`Complete: ${validRacers.length} valid, ${removedRacers.length} removed, ${changes.length} changes`);
  
  return { validRacers, removedRacers, changes };
}

/**
 * Apply USSA minimum points for unranked top-5 finishers.
 * 
 * USSA Rules:
 * - If top-5 finisher has no NRL points (or 990):
 *   - US citizen: Use age-based minimum (200/250/350)
 *   - Foreign citizen: Use FIS points, or age-based minimum if no FIS points
 * - For ties at positions 4/5, use best NRL points among tied
 */
function applyUssaMinimumPoints(racers, changes, options) {
  const { sex = 'M', raceDate, onProgress } = options;
  
  // Get top 5 by rank (handle ties)
  const top5 = racers.filter(r => {
    const rank = parseInt(getField(r, CANONICAL_FIELDS.RANK), 10);
    return rank >= 1 && rank <= 5;
  });
  
  if (top5.length === 0) {
    console.log('[USSA Cleanup] No top-5 finishers found for minimum points calculation');
    return;
  }
  
  console.log(`[USSA Cleanup] Checking ${top5.length} top-5 finishers for minimum points`);
  
  for (const racer of top5) {
    const currentNRL = parseFloat(getField(racer, CANONICAL_FIELDS.USSA_POINTS));
    
    // Skip if they have valid NRL points (not 990 or missing)
    if (currentNRL && currentNRL !== 990 && currentNRL < 990) {
      continue;
    }
    
    // Determine if US citizen (Country === 'USA' or undefined defaults to USA for USSA members)
    const country = getField(racer, CANONICAL_FIELDS.COUNTRY) || 'USA';
    const isUSCitizen = country.toUpperCase() === 'USA';
    
    // Get YOB and calculate age category
    const yob = getField(racer, CANONICAL_FIELDS.YOB);
    const ageCategory = getUssaAgeCategory(yob, raceDate);
    
    // Get FIS points if available (for foreign racers)
    const fisPoints = parseFloat(getField(racer, CANONICAL_FIELDS.FIS_POINTS));
    const validFisPoints = !isNaN(fisPoints) ? fisPoints : null;
    
    // Calculate minimum points
    const minimumPoints = getUssaMinimumPoints(ageCategory, sex, isUSCitizen, validFisPoints);
    
    // Build explanation
    const isWomen = sex === 'F' || sex === 'W';
    let note = `Minimum points for unranked top-5: `;
    if (!isUSCitizen && validFisPoints) {
      note += `Foreign (${country}) - using FIS points`;
    } else if (ageCategory === 'U16') {
      note += `U16 (YOB: ${yob})`;
    } else {
      note += `${isWomen ? 'Women' : 'Men'} ${ageCategory}`;
      if (!isUSCitizen) {
        note += ` (Foreign, no FIS points)`;
      }
    }
    
    const racerName = `${getField(racer, CANONICAL_FIELDS.FIRST_NAME)} ${getField(racer, CANONICAL_FIELDS.LAST_NAME)}`;
    console.log(`[USSA Cleanup] Applying minimum ${minimumPoints} to ${racerName}: ${note}`);
    
    // Apply the minimum points
    changes.push({
      ussaCode: getField(racer, CANONICAL_FIELDS.USSA_CODE),
      field: 'ussaPoints',
      oldValue: currentNRL || 990,
      newValue: minimumPoints,
      note,
    });
    
    racer[CANONICAL_FIELDS.USSA_POINTS] = minimumPoints;
    
    onProgress?.(`Applied ${minimumPoints} points to ${racerName} (${note})`);
  }
}

/**
 * Sleep helper
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetch member data using batch queries (single query returns multiple members).
 */
async function fetchMembersBatch(ussaCodes, onProgress) {
  const results = new Map();
  const batches = chunkArray(ussaCodes, BATCH_SIZE);
  
  console.log(`[USSA Cleanup] Fetching ${ussaCodes.length} members in ${batches.length} batch(es)`);
  
  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    onProgress?.(`Fetching members batch ${i + 1}/${batches.length} (${batch.length} IDs)...`);
    
    try {
      const members = await getMembersByIdsBatch(batch);
      console.log(`[USSA Cleanup] Batch ${i + 1} returned ${members.length} members`);
      
      // Map results by ID
      for (const member of members) {
        const id = String(member.id);
        results.set(id, member);
      }
    } catch (err) {
      console.error(`[USSA Cleanup] Batch ${i + 1} failed:`, err.message);
    }
    
    // Delay between batches
    if (i < batches.length - 1) {
      await sleep(DELAY_BETWEEN_BATCHES);
    }
  }
  
  console.log(`[USSA Cleanup] Got member data for ${results.size}/${ussaCodes.length} racers`);
  return results;
}

/**
 * Fetch rankings using batch queries (single query returns multiple rankings).
 */
async function fetchRankingsBatch(ussaCodes, listCode, season, onProgress) {
  const results = new Map();
  const batches = chunkArray(ussaCodes, BATCH_SIZE);
  
  console.log(`[USSA Cleanup] Fetching rankings for ${ussaCodes.length} members in ${batches.length} batch(es)`);
  
  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    onProgress?.(`Fetching rankings batch ${i + 1}/${batches.length} (${batch.length} IDs)...`);
    
    try {
      const rankings = await getMembersRankingsBatch(batch, listCode, season);
      console.log(`[USSA Cleanup] Rankings batch ${i + 1} returned ${rankings.length} rankings`);
      
      // Map results by memberId
      for (const ranking of rankings) {
        const id = String(ranking.memberId);
        results.set(id, ranking);
      }
    } catch (err) {
      console.error(`[USSA Cleanup] Rankings batch ${i + 1} failed:`, err.message);
    }
    
    // Delay between batches
    if (i < batches.length - 1) {
      await sleep(DELAY_BETWEEN_BATCHES);
    }
  }
  
  console.log(`[USSA Cleanup] Got rankings for ${results.size}/${ussaCodes.length} racers`);
  return results;
}

/**
 * Check if member has active membership for the given season.
 */
function checkMembershipActive(member, season) {
  if (!member) return false;
  
  // Check memberships array
  const memberships = member.memberships || [];
  const hasValidMembership = memberships.some(m => 
    m.season === season && (m.status === 'valid' || m.status === 'Active' || m.status === 'active')
  );
  
  if (hasValidMembership) return true;
  
  // Check seasons array for current flag
  const seasons = member.seasons || [];
  const currentSeason = seasons.find(s => s.season === season);
  if (currentSeason?.flags?.current) return true;
  
  // If we have no membership data, assume valid (be lenient)
  if (memberships.length === 0 && seasons.length === 0) return true;
  
  return false;
}

/**
 * Split array into chunks of specified size.
 */
function chunkArray(array, size) {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

/**
 * Lookup USSA points for a list of USSA codes.
 * @param {Array<string>} ussaCodes - USSA member IDs
 * @param {string} listCode - List code (e.g., "26LIST04")
 * @param {string} discipline - 'distance' | 'sprint' | 'overall'
 * @returns {Promise<Map<string, number>>} Map of USSA code to points
 */
export async function lookupUssaPoints(ussaCodes, listCode, discipline = 'distance') {
  // This would call the crossCountryRankings endpoint
  // For now, return empty map - points lookup is a separate step
  return new Map();
}

/**
 * Search for USSA member by name.
 * Note: This requires the new USSA API proxy which may not be deployed.
 * For now, returns empty array. Use JuryForm's built-in search instead.
 * @param {string} firstName 
 * @param {string} lastName 
 * @returns {Promise<Array>} Matching members
 */
export async function searchUssaMemberByName(firstName, lastName) {
  // TODO: Implement when USSA API proxy is deployed
  console.warn('searchUssaMemberByName: USSA API proxy not configured, returning empty');
  return [];
}

/**
 * Re-rank racers by time (for distance races).
 */
function reRankRacers(racers) {
  const rankCounts = new Map();
  racers.forEach((racer) => {
    const rankNum = parseInt(getField(racer, CANONICAL_FIELDS.RANK), 10);
    if (!Number.isNaN(rankNum) && rankNum > 0) {
      rankCounts.set(rankNum, (rankCounts.get(rankNum) || 0) + 1);
    }
  });
  racers.forEach((racer) => {
    const rankNum = parseInt(getField(racer, CANONICAL_FIELDS.RANK), 10);
    racer.__originalRank = Number.isNaN(rankNum) ? null : rankNum;
    racer.__originalWasTied = !!(racer.__originalRank && rankCounts.get(racer.__originalRank) > 1);
  });

  // Sort by time (handle various time formats)
  racers.sort((a, b) => {
    const timeA = parseTime(getField(a, CANONICAL_FIELDS.TIME));
    const timeB = parseTime(getField(b, CANONICAL_FIELDS.TIME));
    return timeA - timeB;
  });
  
  let position = 0;
  let lastTime = null;
  let lastRank = 0;
  let lastRacer = null;
  
  for (const racer of racers) {
    const status = getField(racer, CANONICAL_FIELDS.STATUS);
    
    // Only rank finishers
    if (!status || status === '' || status === 'QLF') {
      position++;
      const currentTime = getField(racer, CANONICAL_FIELDS.TIME);
      
      if (currentTime !== lastTime) {
        racer[CANONICAL_FIELDS.RANK] = position;
      } else {
        const canKeepTie =
          lastRacer &&
          racer.__originalWasTied &&
          lastRacer.__originalWasTied &&
          racer.__originalRank === lastRacer.__originalRank;
        racer[CANONICAL_FIELDS.RANK] = canKeepTie ? lastRank : position;
      }
      
      lastRank = racer[CANONICAL_FIELDS.RANK];
      lastTime = currentTime;
      lastRacer = racer;
    } else {
      racer[CANONICAL_FIELDS.RANK] = ''; // DNF, DNS, etc.
      lastTime = null;
      lastRacer = null;
    }
  }

  racers.forEach((racer) => {
    delete racer.__originalRank;
    delete racer.__originalWasTied;
  });
}

/**
 * Re-rank sprint heats racers preserving original order.
 * Sprint heats rankings are based on progression through heats (Highest Level),
 * not on time, so we preserve the original order and just reassign sequential ranks.
 * 
 * @param {Array} validRacers - Racers that passed validation (will be sorted in place)
 * @param {Array} originalRacers - Original racer array to determine order
 */
function reRankSprintRacers(validRacers, originalRacers) {
  // Create a map of USSA code to original index
  const originalOrder = new Map();
  originalRacers.forEach((racer, idx) => {
    const ussaCode = String(getField(racer, CANONICAL_FIELDS.USSA_CODE) || '').trim();
    if (ussaCode) {
      originalOrder.set(ussaCode, idx);
    }
  });
  
  // Sort valid racers by their original position
  validRacers.sort((a, b) => {
    const ussaA = String(getField(a, CANONICAL_FIELDS.USSA_CODE) || '').trim();
    const ussaB = String(getField(b, CANONICAL_FIELDS.USSA_CODE) || '').trim();
    const orderA = originalOrder.get(ussaA) ?? Infinity;
    const orderB = originalOrder.get(ussaB) ?? Infinity;
    return orderA - orderB;
  });
  
  // Assign sequential ranks based on sorted order
  let position = 0;
  for (const racer of validRacers) {
    const status = getField(racer, CANONICAL_FIELDS.STATUS);
    
    // Only rank finishers (QLF status or empty)
    if (!status || status === '' || status === 'QLF') {
      position++;
      racer[CANONICAL_FIELDS.RANK] = position;
    } else {
      racer[CANONICAL_FIELDS.RANK] = ''; // DNF, DNS, etc.
    }
  }
  
  console.log(`[USSA Cleanup] Sprint heats re-ranking: assigned ranks 1-${position} preserving original order`);
}

/**
 * Parse time string to seconds for sorting.
 */
function parseTime(timeStr) {
  if (!timeStr) return Infinity;
  
  const str = String(timeStr).trim();
  
  // Handle MM:SS.ss or HH:MM:SS.ss
  const parts = str.split(':');
  
  if (parts.length === 1) {
    return parseFloat(parts[0]) || Infinity;
  } else if (parts.length === 2) {
    return (parseFloat(parts[0]) * 60) + parseFloat(parts[1]) || Infinity;
  } else if (parts.length === 3) {
    return (parseFloat(parts[0]) * 3600) + (parseFloat(parts[1]) * 60) + parseFloat(parts[2]) || Infinity;
  }
  
  return Infinity;
}

const ussaCleanup = {
  cleanUssaData,
  lookupUssaPoints,
  searchUssaMemberByName,
};

export default ussaCleanup;
